import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, auth } from "../../firebaseConfig";
import { toggleCommentLike, toggleReplyLike } from "../../firebase/likesApi";
import { toast } from "react-toastify";

// --- Listeners ---
export const setupCommentLikesListener = createAsyncThunk(
  "commentLikes/setupCommentLikesListener",
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      if (!userId) throw new Error("User ID required");
      return await new Promise((resolve, reject) => {
        try {
          const q = query(
            collection(db, "commentLikes"),
            where("userId", "==", userId),
          );
          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              try {
                // Guard: stop dispatching if user has changed
                const currentUid = auth?.currentUser?.uid || null;
                if (currentUid && currentUid !== userId) return;

                snapshot.docChanges().forEach((change) => {
                  const data =
                    typeof change?.doc?.data === "function"
                      ? change.doc.data()
                      : {};
                  const commentId = data?.commentId || null;
                  if (!commentId) return;
                  const isLiked = change.type !== "removed";
                  dispatch(
                    setCommentLikeStatus({ commentId, isLiked }),
                  );
                });
              } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                  console.error("[setupCommentLikesListener] Error processing snapshot:", err);
                }
              }
            },
            (error) => {
              const msg = error?.message || "Failed to sync comment likes";
              console.error("[setupCommentLikesListener]", msg, error);
              toast.error("Could not sync comment likes. Please refresh.");
            },
          );
          resolve(unsubscribe);
        } catch (e) {
          reject(e);
        }
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const setupReplyLikesListener = createAsyncThunk(
  "commentLikes/setupReplyLikesListener",
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      if (!userId) throw new Error("User ID required");
      return await new Promise((resolve, reject) => {
        try {
          const q = query(
            collection(db, "replyLikes"),
            where("userId", "==", userId),
          );
          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              try {
                // Guard: stop dispatching if user has changed
                const currentUid = auth?.currentUser?.uid || null;
                if (currentUid && currentUid !== userId) return;

                snapshot.docChanges().forEach((change) => {
                  const data =
                    typeof change?.doc?.data === "function"
                      ? change.doc.data()
                      : {};
                  const replyId = data?.replyId || null;
                  if (!replyId) return;
                  const isLiked = change.type !== "removed";
                  dispatch(
                    setReplyLikeStatus({ replyId, isLiked }),
                  );
                });
              } catch (err) {
                if (process.env.NODE_ENV !== "production") {
                  console.error("[setupReplyLikesListener] Error processing snapshot:", err);
                }
              }
            },
            (error) => {
              const msg = error?.message || "Failed to sync reply likes";
              console.error("[setupReplyLikesListener]", msg, error);
              toast.error("Could not sync reply likes. Please refresh.");
            },
          );
          resolve(unsubscribe);
        } catch (e) {
          reject(e);
        }
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// --- Async thunks ---
export const toggleCommentLikeAsync = createAsyncThunk(
  "commentLikes/toggleCommentLike",
  async ({ postId, commentId }, { getState, rejectWithValue }) => {
    try {
      if (typeof commentId === "string" && commentId.startsWith("temp-")) {
        return rejectWithValue({
          message: "Please wait — the comment is still being saved.",
          postId,
          commentId,
        });
      }
      const uid = getState()?.auth?.currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");
      const result = await toggleCommentLike(commentId, uid);
      return { postId, commentId, ...result };
    } catch (error) {
      return rejectWithValue({ message: error.message, postId, commentId });
    }
  },
);

export const toggleReplyLikeAsync = createAsyncThunk(
  "commentLikes/toggleReplyLike",
  async ({ commentId, replyId }, { getState, rejectWithValue }) => {
    try {
      if (typeof replyId === "string" && replyId.startsWith("temp-reply-")) {
        return rejectWithValue({
          message: "Please wait — the reply is still being saved.",
          commentId,
          replyId,
        });
      }
      const uid = getState()?.auth?.currentUser?.uid;
      if (!uid) throw new Error("Not authenticated");
      const result = await toggleReplyLike(replyId, uid);
      return { commentId, replyId, ...result };
    } catch (error) {
      return rejectWithValue({ message: error.message, commentId, replyId });
    }
  },
);

// --- Slice ---
const initialState = {
  // Like-status lookup maps (fix for "vanishing like" bug)
  likedCommentIds: {}, // { [commentId]: true }
  likedReplyIds: {}, // { [replyId]: true }
  // Pending states
  commentLikePending: {}, // { [commentId]: boolean }
  replyLikePending: {}, // { [replyId]: boolean }
  commentLikeRollback: {}, // { [commentId]: boolean } pre-toggle liked state
  replyLikeRollback: {}, // { [replyId]: boolean }
  // Listeners
  commentLikesListener: null,
  replyLikesListener: null,
};

const commentLikesSlice = createSlice({
  name: "commentLikes",
  initialState,
  reducers: {
    setCommentLikeStatus: (state, action) => {
      const { commentId, isLiked } = action.payload || {};
      if (!commentId) return;

      if (isLiked) {
        state.likedCommentIds[commentId] = true;
      } else {
        delete state.likedCommentIds[commentId];
      }
    },
    setReplyLikeStatus: (state, action) => {
      const { replyId, isLiked } = action.payload || {};
      if (!replyId) return;

      if (isLiked) {
        state.likedReplyIds[replyId] = true;
      } else {
        delete state.likedReplyIds[replyId];
      }
    },
    cleanupCommentLikesListener: (state) => {
      if (state.commentLikesListener) {
        try {
          state.commentLikesListener();
        } catch {}
        state.commentLikesListener = null;
      }
    },
    cleanupReplyLikesListener: (state) => {
      if (state.replyLikesListener) {
        try {
          state.replyLikesListener();
        } catch {}
        state.replyLikesListener = null;
      }
    },
    clearAllLikes: (state) => {
      state.likedCommentIds = {};
      state.likedReplyIds = {};
      state.commentLikePending = {};
      state.replyLikePending = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Setup listeners
      .addCase(setupCommentLikesListener.pending, (state) => {
        if (state.commentLikesListener) {
          try {
            state.commentLikesListener();
          } catch {}
        }
        state.commentLikesListener = null;
      })
      .addCase(setupCommentLikesListener.fulfilled, (state, action) => {
        state.commentLikesListener = action.payload;
      })
      .addCase(setupCommentLikesListener.rejected, () => {})

      .addCase(setupReplyLikesListener.pending, (state) => {
        if (state.replyLikesListener) {
          try {
            state.replyLikesListener();
          } catch {}
        }
        state.replyLikesListener = null;
      })
      .addCase(setupReplyLikesListener.fulfilled, (state, action) => {
        state.replyLikesListener = action.payload;
      })
      .addCase(setupReplyLikesListener.rejected, () => {})

      // Toggle comment like (optimistic heart)
      .addCase(toggleCommentLikeAsync.pending, (state, action) => {
        const { commentId } = action.meta.arg || {};
        if (!commentId) return;
        if (!state.commentLikeRollback) state.commentLikeRollback = {};
        state.commentLikePending[commentId] = true;
        state.commentLikeRollback[commentId] =
          !!state.likedCommentIds[commentId];
        if (state.likedCommentIds[commentId]) {
          delete state.likedCommentIds[commentId];
        } else {
          state.likedCommentIds[commentId] = true;
        }
      })
      .addCase(toggleCommentLikeAsync.fulfilled, (state, action) => {
        const { commentId, isLiked } = action.payload;
        if (commentId) {
          state.commentLikePending[commentId] = false;
          delete state.commentLikeRollback[commentId];
        }

        if (isLiked) {
          state.likedCommentIds[commentId] = true;
        } else {
          delete state.likedCommentIds[commentId];
        }
      })
      .addCase(toggleCommentLikeAsync.rejected, (state, action) => {
        const { commentId } = action.payload || action.meta.arg || {};
        if (commentId) {
          state.commentLikePending[commentId] = false;
          if (state.commentLikeRollback && commentId in state.commentLikeRollback) {
            const wasLiked = state.commentLikeRollback[commentId];
            if (wasLiked) {
              state.likedCommentIds[commentId] = true;
            } else {
              delete state.likedCommentIds[commentId];
            }
            delete state.commentLikeRollback[commentId];
          }
        }

        const msg = action.payload?.message || "Failed to like comment";
        toast.error(msg);
      })

      // Toggle reply like (optimistic heart)
      .addCase(toggleReplyLikeAsync.pending, (state, action) => {
        const { replyId } = action.meta.arg || {};
        if (!replyId) return;
        if (!state.replyLikeRollback) state.replyLikeRollback = {};
        state.replyLikePending[replyId] = true;
        state.replyLikeRollback[replyId] = !!state.likedReplyIds[replyId];
        if (state.likedReplyIds[replyId]) {
          delete state.likedReplyIds[replyId];
        } else {
          state.likedReplyIds[replyId] = true;
        }
      })
      .addCase(toggleReplyLikeAsync.fulfilled, (state, action) => {
        const { replyId, isLiked } = action.payload;
        if (replyId) {
          state.replyLikePending[replyId] = false;
          delete state.replyLikeRollback[replyId];
        }

        if (isLiked) {
          state.likedReplyIds[replyId] = true;
        } else {
          delete state.likedReplyIds[replyId];
        }
      })
      .addCase(toggleReplyLikeAsync.rejected, (state, action) => {
        const { replyId } = action.payload || action.meta.arg || {};
        if (replyId) {
          state.replyLikePending[replyId] = false;
          if (state.replyLikeRollback && replyId in state.replyLikeRollback) {
            const wasLiked = state.replyLikeRollback[replyId];
            if (wasLiked) {
              state.likedReplyIds[replyId] = true;
            } else {
              delete state.likedReplyIds[replyId];
            }
            delete state.replyLikeRollback[replyId];
          }
        }

        const msg = action.payload?.message || "Failed to like reply";
        toast.error(msg);
      });
  },
});

export const {
  setCommentLikeStatus,
  setReplyLikeStatus,
  cleanupCommentLikesListener,
  cleanupReplyLikesListener,
  clearAllLikes,
} = commentLikesSlice.actions;

export default commentLikesSlice.reducer;
